import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import DesignServicesIcon from '@mui/icons-material/DesignServices'
import { Box, Button, SxProps, Theme, Typography, alpha, useTheme } from '@mui/material'
import { common } from '@mui/material/colors'

/** Which CTA fired the (demo) submission, used to theme + caption the confirmation. */
type CtaKind = 'genai' | 'studio'

/** How long the animated confirmation holds before it starts animating out. */
const CONFIRMATION_HOLD_MS = 5000

/** Duration of the outro animation; kept in sync with `uaSentPanelOut` below. */
const CONFIRMATION_OUTRO_MS = 360

interface UAInsightsDialogActionsProps {
  /** Disables both CTAs while the dialog has no data to act on. */
  disabled?: boolean
}

/**
 * The shared "Prompt to GenAI" / "Contact Creative Studio" call-to-action pair
 * rendered in the footer of every UA Insights motivation drilldown dialog.
 *
 * Both buttons share a playful hover treatment: the button springs up with a
 * colour-matched glow, a diagonal light "shine" sweeps across the surface, and
 * the leading icon comes alive (the GenAI sparkle spins + twinkles, the
 * Creative Studio pen tilts). All motion is gated behind
 * `prefers-reduced-motion` so it stays calm for users who opt out.
 *
 * Clicking either CTA swaps the row for an animated confirmation (a popping
 * check badge with an expanding ring + twinkling sparkles) that reassures the
 * user their request "went through" while making clear this is still a demo —
 * nothing is actually submitted. The confirmation auto-reverts so the other
 * CTA can still be tried.
 */
export const UAInsightsDialogActions: FC<UAInsightsDialogActionsProps> = ({ disabled }) => {
  const { t } = useTranslation('ua-insights')
  const theme = useTheme()
  const [sent, setSent] = useState<CtaKind | null>(null)
  // `exiting` drives the outro: the panel holds for 5s, plays its exit
  // animation, then unmounts back to the CTAs.
  const [exiting, setExiting] = useState(false)
  const holdTimer = useRef<ReturnType<typeof setTimeout>>()
  const outroTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(
    () => () => {
      clearTimeout(holdTimer.current)
      clearTimeout(outroTimer.current)
    },
    []
  )

  const handleSend = (kind: CtaKind): void => {
    clearTimeout(holdTimer.current)
    clearTimeout(outroTimer.current)
    setExiting(false)
    setSent(kind)
    holdTimer.current = setTimeout(() => {
      setExiting(true)
      outroTimer.current = setTimeout(() => {
        setSent(null)
        setExiting(false)
      }, CONFIRMATION_OUTRO_MS)
    }, CONFIRMATION_HOLD_MS)
  }

  const accent = sent === 'genai' ? theme.palette.secondary.main : theme.palette.primary.main

  return (
    // The button row always stays in normal flow so it — and only it — defines
    // the footer height. The confirmation is an absolutely-positioned overlay,
    // so pressing a CTA can't change the footer height. That keeps the dialog's
    // fixed-height paper from re-flowing its scrollable content (which would
    // otherwise make the scrollbar appear/disappear and the layout twitch).
    <Box sx={actionsShellSx}>
      <Box className="ua-cta-row" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, visibility: sent ? 'hidden' : 'visible' }}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AutoAwesomeIcon className="ua-cta-icon" />}
          disabled={disabled || !!sent}
          onClick={() => handleSend('genai')}
          sx={actionSx(theme.palette.secondary.main, 'rotate(180deg) scale(1.25)')}
        >
          {t('prompt_to_genai')}
        </Button>
        <Button
          variant="contained"
          startIcon={<DesignServicesIcon className="ua-cta-icon" />}
          disabled={disabled || !!sent}
          onClick={() => handleSend('studio')}
          sx={actionSx(theme.palette.primary.main, 'rotate(-14deg) scale(1.2)')}
        >
          {t('contact_creative_studio')}
        </Button>
      </Box>
      {sent && (
        <Box className="ua-sent-overlay">
          <SentConfirmation
            accent={accent}
            exiting={exiting}
            title={sent === 'genai' ? t('cta_genai_sent_title') : t('cta_studio_sent_title')}
            note={t('cta_sent_demo_note')}
          />
        </Box>
      )}
    </Box>
  )
}

/**
 * Full-width shell that centres the button row and hosts the confirmation
 * overlay. `position: relative` anchors the absolutely-positioned overlay; the
 * shell's height is driven entirely by the in-flow button row so it stays
 * constant across states.
 */
const actionsShellSx: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  '& .ua-sent-overlay': {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}

interface SentConfirmationProps {
  /** Colour the burst is tinted with — the clicked button's own accent. */
  accent: string
  /** When `true`, plays the outro animation just before the panel unmounts. */
  exiting: boolean
  /** Headline reassuring the user the (demo) request landed. */
  title: string
  /** Caption making clear nothing was really submitted. */
  note: string
}

interface SparkleSpec {
  top?: number
  left?: number
  right?: number
  bottom?: number
  size: number
  delay: number
}

/** Decorative sparkles that pop out around the badge, each on a staggered beat. */
const SPARKLES: SparkleSpec[] = [
  { top: -6, left: -10, size: 14, delay: 220 },
  { top: -2, right: -12, size: 11, delay: 320 },
  { bottom: -8, left: 4, size: 12, delay: 400 },
]

/**
 * The animated "request sent" panel shown in place of the CTAs. The check
 * badge springs in, an expanding ring radiates out behind it, and a few
 * sparkles twinkle on a stagger — all colour-matched to the button that was
 * pressed and gated behind `prefers-reduced-motion`. On `exiting` the whole
 * panel scales + fades away (outro) before unmounting.
 */
const SentConfirmation: FC<SentConfirmationProps> = ({ accent, exiting, title, note }) => (
  <Box className={exiting ? 'ua-exiting' : undefined} sx={sentContainerSx(accent)}>
    <Box className="ua-sent-badge">
      <Box className="ua-sent-ring" />
      <CheckRoundedIcon className="ua-sent-check" />
      {SPARKLES.map((sparkle, index) => (
        <AutoAwesomeIcon
          key={index}
          className="ua-sent-sparkle"
          sx={{
            position: 'absolute',
            top: sparkle.top,
            left: sparkle.left,
            right: sparkle.right,
            bottom: sparkle.bottom,
            fontSize: sparkle.size,
            // Read by the container's `animation` shorthand to stagger each
            // sparkle (a bare `animationDelay` here loses to the shorthand).
            '--ua-sparkle-delay': `${sparkle.delay}ms`,
          }}
        />
      ))}
    </Box>
    <Box className="ua-sent-copy">
      <Typography className="ua-sent-title" variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
        {title}
      </Typography>
      <Typography className="ua-sent-note" variant="caption" color="text.secondary">
        {note}
      </Typography>
    </Box>
  </Box>
)

/**
 * Builds the animated `sx` for one CTA. `glow` tints the hover halo to the
 * button's own colour and `iconTransform` is the springy resting→hover pose
 * for its leading icon, so each button feels distinct while sharing the same
 * lift + light-sweep choreography.
 */
const actionSx = (glow: string, iconTransform: string): SxProps<Theme> => ({
  position: 'relative',
  // Clip the light sweep to the button's rounded rectangle.
  overflow: 'hidden',
  fontWeight: 700,
  px: 2.5,
  willChange: 'transform',
  transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease',
  // Diagonal light band parked just off the left edge until hover.
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(115deg, transparent 35%, ${alpha(common.white, 0.5)} 50%, transparent 65%)`,
    transform: 'translateX(-150%)',
    // Snap back instantly on leave so there's no reverse sweep — only the
    // hover state re-enables the timed transition below.
    transition: 'transform 0s',
    pointerEvents: 'none',
  },
  '& .ua-cta-icon': {
    transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  '@media (prefers-reduced-motion: no-preference)': {
    '&:hover:not(.Mui-disabled)': {
      transform: 'translateY(-2px) scale(1.015)',
      boxShadow: `0 10px 22px -8px ${alpha(glow, 0.65)}`,
      '&::before': {
        transform: 'translateX(150%)',
        transition: 'transform 0.7s ease',
      },
      '& .ua-cta-icon': {
        transform: iconTransform,
      },
    },
    '&:active:not(.Mui-disabled)': {
      transform: 'translateY(0) scale(0.99)',
    },
  },
})

/**
 * Builds the `sx` for the confirmation panel. Every piece (panel, badge, ring,
 * sparkles, copy) lands in its final visible state by default so users with
 * `prefers-reduced-motion` simply see the result; the keyframes only run for
 * everyone else, gated by the media query at the bottom.
 */
const sentContainerSx = (accent: string): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.75,
  px: 1,
  textAlign: 'left',
  '& .ua-sent-badge': {
    position: 'relative',
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: common.white,
    background: `linear-gradient(135deg, ${accent}, ${alpha(accent, 0.78)})`,
    boxShadow: `0 8px 20px -6px ${alpha(accent, 0.6)}`,
  },
  '& .ua-sent-check': {
    fontSize: 26,
  },
  '& .ua-sent-ring': {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    border: `2px solid ${alpha(accent, 0.55)}`,
    opacity: 0,
    pointerEvents: 'none',
  },
  '& .ua-sent-sparkle': {
    color: accent,
    opacity: 0,
    pointerEvents: 'none',
  },
  '@keyframes uaSentPanelIn': {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '@keyframes uaSentBadgePop': {
    '0%': { opacity: 0, transform: 'scale(0.4)' },
    '60%': { opacity: 1, transform: 'scale(1.12)' },
    '100%': { opacity: 1, transform: 'scale(1)' },
  },
  '@keyframes uaSentCheckIn': {
    from: { opacity: 0, transform: 'scale(0.3)' },
    to: { opacity: 1, transform: 'scale(1)' },
  },
  '@keyframes uaSentRing': {
    from: { opacity: 0.55, transform: 'scale(0.6)' },
    to: { opacity: 0, transform: 'scale(2.4)' },
  },
  '@keyframes uaSentSparkle': {
    '0%': { opacity: 0, transform: 'scale(0) rotate(-30deg)' },
    '55%': { opacity: 1, transform: 'scale(1.15) rotate(8deg)' },
    '100%': { opacity: 0.9, transform: 'scale(1) rotate(0deg)' },
  },
  '@keyframes uaSentCopyIn': {
    from: { opacity: 0, transform: 'translateX(-6px)' },
    to: { opacity: 1, transform: 'translateX(0)' },
  },
  // Outro: the whole panel drops + fades while the badge gives one last pop
  // before shrinking away.
  '@keyframes uaSentPanelOut': {
    from: { opacity: 1, transform: 'translateY(0) scale(1)' },
    to: { opacity: 0, transform: 'translateY(4px) scale(0.92)' },
  },
  '@keyframes uaSentBadgeOut': {
    '0%': { transform: 'scale(1)' },
    '35%': { transform: 'scale(1.1)' },
    '100%': { opacity: 0, transform: 'scale(0.5)' },
  },
  '@media (prefers-reduced-motion: no-preference)': {
    animation: 'uaSentPanelIn 0.32s ease both',
    '& .ua-sent-badge': {
      animation: 'uaSentBadgePop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both',
    },
    '& .ua-sent-ring': {
      animation: 'uaSentRing 0.85s ease-out 0.1s both',
    },
    '& .ua-sent-check': {
      animation: 'uaSentCheckIn 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) 0.12s both',
    },
    '& .ua-sent-sparkle': {
      animation: 'uaSentSparkle 0.6s ease-out var(--ua-sparkle-delay, 0ms) both',
    },
    '& .ua-sent-copy': {
      animation: 'uaSentCopyIn 0.4s ease 0.18s both',
    },
    // Higher-specificity override that swaps the intro for the outro.
    '&.ua-exiting': {
      animation: `uaSentPanelOut ${CONFIRMATION_OUTRO_MS}ms ease forwards`,
      '& .ua-sent-badge': {
        animation: `uaSentBadgeOut ${CONFIRMATION_OUTRO_MS}ms ease forwards`,
      },
    },
  },
})
